import { EmergencyContact } from "@app/project/model/emergency-contact";
import { ActionPayloadGeneric } from "@app/root-store/actions";
import {
    createActionSet,
    createCrudActionSets,
    createDeleteAllPromptActionSet,
    createDeletePromptActionSet,
    createNoPropsAction,
} from "@app/root-store/actions/action-creators";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";

/**
 * A payload used when performing an operation on a single entity
 */
export type ActionPayload = ActionPayloadGeneric & {
    emergencyContact: EmergencyContact;
};

/**
 * A payload used when performing an operation on many entities
 */
type BulkActionPayload = ActionPayloadGeneric & {
    emergencyContacts: EmergencyContact[];
};

/**
 * A payload used when performing a delete all operation
 */
export type DeleteAllActionPayload = ActionPayloadGeneric & {
    emergencyContacts: EmergencyContact[];
};

/**
 * A payload used when loading an entity
 */
interface LoadActionPayload {
    projectId: number;
}

/**
 * An action result that returns a collection of entities
 */
export interface BulkActionResult {
    emergencyContacts: ClareDbCollection<EmergencyContact>;
}

/**
 * An action result that returns a single entity
 */
export interface ActionResult {
    emergencyContact: EmergencyContact;
}

/**
 * @description Used in the action creators to generate an action type like:
 * "[Entity Management] Load Entity"
 */
const actionCategory = "Emergency Contact";

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
const {
    load: loadSet,
    create: createSet,
    update: updateSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);

/**
 * Create each action from the deconstructed action sets created by createCrudActionSets.
 * Each action is strongly typed and should be used in accordance with the NgRx docs.
 * Any action is optional and can be omitted by simply not desctructuring it.
 */
export const { request: load, success: loadSuccess, failure: loadFailure } = loadSet;
export const { request: create, success: createSuccess, failure: createFailure } = createSet;
export const { request: update, success: updateSuccess, failure: updateFailure } = updateSet;
export const { request: deleteItem, success: deleteSuccess, failure: deleteFailure } = deleteSet;

/**
 * An action set for a bulk update operation
 */
export const {
    request: bulkUpdate,
    success: bulkUpdateSuccess,
    failure: bulkUpdateFailure,
} = createActionSet<BulkActionPayload, BulkActionResult>(actionCategory, "Bulk Update");

/**
 * An action set to prompt a user when deleting an item
 */
export const { confirm: deleteConfirmation, dismiss: deleteDismiss } = createDeletePromptActionSet<ActionPayload>(actionCategory);

export const {
    request: deleteAll,
    success: deleteAllSuccess,
    failure: deleteAllFailure,
} = createActionSet<DeleteAllActionPayload>(actionCategory, "Bulk Delete");
export const { confirm: deleteAllConfirmation, dismiss: deleteAllDismiss } =
    createDeleteAllPromptActionSet<DeleteAllActionPayload>(actionCategory);

/**
 * An action to clear the data in the store
 */
export const clear = createNoPropsAction(actionCategory, "Clear State");
